import type { ClientStudioDoc } from "api-types/studioDocs/studioDocResponses";
import type { Block } from "building-types/blocks/blockTypes";
import type { HomeAttributes } from "building-types/building/homeAttributes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { Doc } from "building-types/doc";
import {
  BASE_PRESET_NAME,
  DEFAULT_OPTION_CHOICES,
  DEFAULT_STUDIO_DOC_NAME,
} from "building-types/doc/docDefaultConstants";
import type { Group } from "building-types/groups/group";
import { stackedLevelPlacementNew } from "building-types/levels/levelTypes";
import type { DocOptionSet } from "building-types/options/optionSet";
import { PresetType } from "building-types/options/preset";
import type { Preset } from "building-types/options/preset";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import { CURRENT_GABLE_ROOF_VERSION } from "building-types/roofs/roofSystemTypes";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { SettingsSetBehaviorType } from "building-types/settings/settingsSetBehaviorType";
import { SettingsType } from "building-types/settings/settingsType";
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import { type ViewTemplate } from "building-types/views/planSets";
import { sheetGeometryCategoriesExcludedFromDefault3DViewTemplates } from "building-types/views/sheetGeometryCategory";
import { viewTemplateUICategoriesForViewType } from "building-types/views/sheetGeometryCategoryTools";
import { SnapEntity } from "building-types/views/snapEntity";
import { SnapKind } from "building-types/views/snapKind";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { snakeCaseToTitleCase } from "common/utils/strings";
import uid, { withSeededUids } from "common/utils/uid";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import {
  builtInCipComponents,
  builtInMasonryComponents,
  builtInSheathingComponents,
  builtInSlabFloorComponents,
} from "./defaults/assemblyComponentDefaults";
import {
  builtInCipComponentsGroupId,
  builtInMasonryComponentsGroupId,
  builtInSheathingComponentsGroupId,
  builtInSlabFloorComponentsGroupId,
} from "./defaults/assemblyComponentDefaults";
import {
  builtInFrameFloorAssemblies,
  builtInFrameFloorAssembliesGroupId,
  builtInSlabFloorAssemblies,
  builtInSlabFloorAssembliesGroupId,
} from "./defaults/defaultFloorAssembly";
import {
  builtInWallPlaneAssemblies,
  builtInWallPlaneAssemblyGroupForId,
  type BuiltInWallPlaneAssemblyId,
  wallPlaneCoreAssemblyGroupId,
  wallPlaneExteriorAssemblyGroupId,
  wallPlaneFoundationAssemblyGroupId,
  wallPlaneInteriorAssemblyGroupId,
} from "./defaults/defaultWallPlaneAssembly";
import { DEFAULT_EXTERIOR_PALETTE_ID, DEFAULT_INTERIOR_PALETTE_ID } from "./defaults/paletteDefaults";
import { docInitDefaultViews } from "./docInitViews";
import { docSyncSettingsOptionSets } from "./docSyncFields";
import { blockNew } from "./elements/blockNew";
import {
  builtInFoundationAssemblies,
  builtInFoundationAssembliesGroupId,
} from "./elements/foundations/defaultFoundationAssemblies";
import {
  builtInFoundationComponents,
  builtInFoundationComponentsGroupId,
} from "./elements/foundations/defaultFoundationComponents";
import { builtInFramingComponents, builtInFramingComponentsGroupId } from "./elements/framing/defaultFramingComponents";
import { builtInHeaderAssembliesGroupId, builtInHeaderAssemblies } from "./elements/framing/defaultHeaderAssembly";
import { iJoistComponentsGroupId, isBuiltInIJoistComponent } from "./elements/framing/defaultIJoistComponents";
import { builtInPropsGroupId } from "./elements/propCatalog/builtInProps";
import { defaultProfileEntity } from "./elements/trim/defaultTrimProfiles";
import { copyForElevationOptionSet, defaultElevationNew } from "./elevation";
import { defaultLabelTable } from "./entity/defaultLabelTable";
import { emptyEntitySet } from "./entity/entitySet";
import { CURRENT_SCHEMA_VERSION } from "./migrate/docMigrate";
import { SystemOptionSet } from "./options/SystemOptionSetEnum";
import { FloorPlanTagOrder } from "./planGeometryBuiltInTagOrders";
import { STAIR_CUT_HEIGHT } from "./plans/floorplanConstants";
import { roofNew } from "./roofSystemTools";
import { BASE_SETTINGS_SETS_NAME } from "./settings/defaultSettings";
import { builtInDatums, builtInDatumsGroupId } from "./utils/datumTools";
import { viewTemplateNew } from "./viewTemplateTools";

// Default snap configurations.
export const DEFAULT_SNAP_KINDS = Object.freeze([
  SnapKind.GRID,
  SnapKind.VERTICAL,
  SnapKind.HORIZONTAL,
  SnapKind.VERTEX,
  SnapKind.MID,
]);
export const DEFAULT_SNAP_ENTITIES = Object.freeze([
  SnapEntity.UNDERLAY,
  SnapEntity.ROOM,
  SnapEntity.REFERENCE_GEOM,
  SnapEntity.CMD,
  SnapEntity.ANNOTATION,
  SnapEntity.STAIR,
  SnapEntity.RAILING,
  SnapEntity.PORCH_COLUMN,
  SnapEntity.CEILING,
  SnapEntity.SHAPE_EDIT_MODE,
  SnapEntity.SITE_SHAPE,
  SnapEntity.VIEWPORT,
  SnapEntity.CONTENT_TEMPLATE_INSTANCE,
  SnapEntity.DATA_TABLE,
  SnapEntity.SECTION_VIEW,
  SnapEntity.FOUNDATION,
]);

export const defaultMirrorOptionSet: DocOptionSet = {
  id: SystemOptionSet.MIRROR,
  sortIndex: 0,
  defaultValue: "false",
  name: "Mirror",
  entityType: "OPTION_SET",
  salesCategory: SalesCategory.UNCATEGORIZED,
  optionSetType: "SYSTEM",
  options: {
    true: {
      id: "true",
      parentId: SystemOptionSet.MIRROR,
      name: "Mirror home",
      sortIndex: 0,
      entityType: "OPTION",
    },
    false: {
      id: "false",
      parentId: SystemOptionSet.MIRROR,
      name: "Don't mirror home",
      sortIndex: 1,
      entityType: "OPTION",
    },
  },
};

const docDefaultPreset: Preset = {
  id: BASE_PRESET_NAME,
  entityType: "PRESET",
  name: BASE_PRESET_NAME,
  sortIndex: 0,
  type: PresetType.PRESETS,
  optionChoices: DEFAULT_OPTION_CHOICES,
};

const defaultOptionSets: Doc["optionSets"] = Object.freeze({
  [SystemOptionSet.MIRROR]: defaultMirrorOptionSet,
});

// an empty frozen object that ensures immutability of Doc tables
const emptyObject = Object.freeze({});

export function defaultDoc(name?: string): Doc {
  const exteriorMaterialSettingsUid = uid();
  const interiorMaterialSettingsUid = uid();
  const wainscotingSettingsUid = uid();
  const roofSettingsId = uid();
  const levelUid = uid();
  const blocks = defaultBlockTable(levelUid);
  const defaultSettingsSetId = uid();
  const defaultElevationId = uid();

  const elevations = Object.freeze({
    [defaultElevationId]: {
      ...defaultElevationNew({ id: defaultElevationId }),
    },
  });

  const defaultSettingsSet: SettingsSet = {
    entityType: "SETTINGS_SET",
    id: defaultSettingsSetId,
    name: BASE_SETTINGS_SETS_NAME,
    sortIndex: 0,
    type: SettingsSetBehaviorType.GLOBAL,
  };

  const defaultHomeAttributes: HomeAttributes = {
    garageHandingDirection: "left",
  };

  // built-in groups
  const builtInPropsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Props",
    id: builtInPropsGroupId,
    sortIndex: 0,
    isBuiltIn: true,
    isDeletable: true,
  };

  const builtInDatumsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Datums",
    id: builtInDatumsGroupId,
    sortIndex: 1,
    isBuiltIn: true,
  };

  const builtInFramingComponentsGroup: Group = {
    entityType: "GROUP",
    name: "Dimensional Lumber",
    id: builtInFramingComponentsGroupId,
    sortIndex: 2,
    isBuiltIn: true,
  };

  const iJoistComponentsGroup: Group = {
    entityType: "GROUP",
    name: "I-Joists",
    id: iJoistComponentsGroupId,
    sortIndex: 2,
    isBuiltIn: true,
  };

  const builtInHeaderAssembliesGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Header Assemblies",
    id: builtInHeaderAssembliesGroupId,
    sortIndex: 3,
    isBuiltIn: true,
  };

  const builtInFoundationComponentsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Foundation Components",
    id: builtInFoundationComponentsGroupId,
    sortIndex: 4,
    isBuiltIn: true,
  };

  const builtInFoundationAssembliesGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Foundation Assemblies",
    id: builtInFoundationAssembliesGroupId,
    sortIndex: 5,
    isBuiltIn: true,
  };

  const builtInFrameFloorAssembliesGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Frame Floor Assemblies",
    id: builtInFrameFloorAssembliesGroupId,
    sortIndex: 4,
    isBuiltIn: true,
  };

  const builtInSlabFloorAssembliesGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Slab Floor Assemblies",
    id: builtInSlabFloorAssembliesGroupId,
    sortIndex: 5,
    isBuiltIn: true,
  };

  const builtInSlabComponentsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Slab Components",
    id: builtInSlabFloorComponentsGroupId,
    sortIndex: 6,
    isBuiltIn: true,
  };

  const builtInSheathingComponentsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Sheathing Components",
    id: builtInSheathingComponentsGroupId,
    sortIndex: 7,
    isBuiltIn: true,
  };

  const builtInMasonryComponentsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in Masonry Components",
    id: builtInMasonryComponentsGroupId,
    sortIndex: 8,
    isBuiltIn: true,
  };

  const builtInCipComponentsGroup: Group = {
    entityType: "GROUP",
    name: "Built-in CIP Components",
    id: builtInCipComponentsGroupId,
    sortIndex: 9,
    isBuiltIn: true,
  };

  const wallPlaneCoreAssemblyGroup: Group = {
    entityType: "GROUP",
    name: "Core Assemblies",
    id: wallPlaneCoreAssemblyGroupId,
    sortIndex: 10,
    isContentMutableBuiltIn: true,
  };

  // Wall plane content mutable built-ins are seeded in new docs only (wallPlanes feature flag).
  // Existing docs are not migrated; this feature requires a new doc.
  const wallPlaneInteriorAssemblyGroup: Group = {
    entityType: "GROUP",
    name: "Interior Assemblies",
    id: wallPlaneInteriorAssemblyGroupId,
    sortIndex: 11,
    isContentMutableBuiltIn: true,
  };

  const wallPlaneExteriorAssemblyGroup: Group = {
    entityType: "GROUP",
    name: "Exterior Assemblies",
    id: wallPlaneExteriorAssemblyGroupId,
    sortIndex: 12,
    isContentMutableBuiltIn: true,
  };

  const wallPlaneFoundationAssemblyGroup: Group = {
    entityType: "GROUP",
    name: "Foundation Assemblies",
    id: wallPlaneFoundationAssemblyGroupId,
    sortIndex: 13,
    isContentMutableBuiltIn: true,
  };

  const defaultGroups: Table<Group> = {
    [builtInPropsGroup.id]: builtInPropsGroup,
    [builtInDatumsGroup.id]: builtInDatumsGroup,
    [builtInFramingComponentsGroup.id]: builtInFramingComponentsGroup,
    [iJoistComponentsGroup.id]: iJoistComponentsGroup,
    [builtInHeaderAssembliesGroup.id]: builtInHeaderAssembliesGroup,
    [builtInFoundationComponentsGroup.id]: builtInFoundationComponentsGroup,
    [builtInFoundationAssembliesGroup.id]: builtInFoundationAssembliesGroup,
    [builtInFrameFloorAssembliesGroup.id]: builtInFrameFloorAssembliesGroup,
    [builtInSlabFloorAssembliesGroup.id]: builtInSlabFloorAssembliesGroup,
    [builtInSlabComponentsGroup.id]: builtInSlabComponentsGroup,
    [builtInSheathingComponentsGroup.id]: builtInSheathingComponentsGroup,
    [builtInMasonryComponentsGroup.id]: builtInMasonryComponentsGroup,
    [builtInCipComponentsGroup.id]: builtInCipComponentsGroup,
    [wallPlaneCoreAssemblyGroup.id]: wallPlaneCoreAssemblyGroup,
    [wallPlaneInteriorAssemblyGroup.id]: wallPlaneInteriorAssemblyGroup,
    [wallPlaneExteriorAssemblyGroup.id]: wallPlaneExteriorAssemblyGroup,
    [wallPlaneFoundationAssemblyGroup.id]: wallPlaneFoundationAssemblyGroup,
  };

  // entity groups for built-in datums
  const defaultDatumsEntityGroups: Table<Group["id"]> = Object.keys(builtInDatums).reduce(
    (acc, datumId) => {
      acc[docEntityHandleHash({ type: "DATUM", id: datumId })] = builtInDatumsGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultFramingComponentsEntityGroups: Table<Group["id"]> = Object.keys(builtInFramingComponents).reduce(
    (acc, componentId) => {
      const groupId = isBuiltInIJoistComponent(componentId)
        ? iJoistComponentsGroup.id
        : builtInFramingComponentsGroup.id;
      acc[docEntityHandleHash({ type: "FRAMING_COMPONENT", id: componentId })] = groupId;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultHeaderAssembliesEntityGroups: Table<Group["id"]> = Object.keys(builtInHeaderAssemblies).reduce(
    (acc, assemblyId) => {
      acc[docEntityHandleHash({ type: "HEADER_ASSEMBLY", id: assemblyId })] = builtInHeaderAssembliesGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultFoundationComponentsEntityGroups: Table<Group["id"]> = Object.keys(builtInFoundationComponents).reduce(
    (acc, componentId) => {
      acc[docEntityHandleHash({ type: "FOUNDATION_COMPONENT", id: componentId })] = builtInFoundationComponentsGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultFoundationAssembliesEntityGroups: Table<Group["id"]> = Object.keys(builtInFoundationAssemblies).reduce(
    (acc, assemblyId) => {
      acc[docEntityHandleHash({ type: "FOUNDATION_ASSEMBLY", id: assemblyId })] = builtInFoundationAssembliesGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultFrameFloorAssembliesEntityGroups: Table<Group["id"]> = Object.keys(builtInFrameFloorAssemblies).reduce(
    (acc, assemblyId) => {
      acc[docEntityHandleHash({ type: "FRAME_FLOOR_ASSEMBLY", id: assemblyId })] = builtInFrameFloorAssembliesGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultSlabFloorAssembliesEntityGroups: Table<Group["id"]> = Object.keys(builtInSlabFloorAssemblies).reduce(
    (acc, assemblyId) => {
      acc[docEntityHandleHash({ type: "SLAB_FLOOR_ASSEMBLY", id: assemblyId })] = builtInSlabFloorAssembliesGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultSlabComponentsEntityGroups: Table<Group["id"]> = Object.keys(builtInSlabFloorComponents).reduce(
    (acc, componentId) => {
      acc[docEntityHandleHash({ type: "SLAB_COMPONENT", id: componentId })] = builtInSlabComponentsGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultMasonryComponentsEntityGroups: Table<Group["id"]> = Object.keys(builtInMasonryComponents).reduce(
    (acc, componentId) => {
      acc[docEntityHandleHash({ type: "MASONRY_COMPONENT", id: componentId })] = builtInMasonryComponentsGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultSheathingComponentsEntityGroups: Table<Group["id"]> = Object.keys(builtInSheathingComponents).reduce(
    (acc, componentId) => {
      acc[docEntityHandleHash({ type: "SHEATHING_COMPONENT", id: componentId })] = builtInSheathingComponentsGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultCipComponentsEntityGroups: Table<Group["id"]> = Object.keys(builtInCipComponents).reduce(
    (acc, componentId) => {
      acc[docEntityHandleHash({ type: "CIP_COMPONENT", id: componentId })] = builtInCipComponentsGroup.id;
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultWallPlaneAssembliesEntityGroups: Table<Group["id"]> = (
    Object.keys(builtInWallPlaneAssemblies) as BuiltInWallPlaneAssemblyId[]
  ).reduce(
    (acc, assemblyId) => {
      acc[docEntityHandleHash({ type: "WALL_PLANE_ASSEMBLY", id: assemblyId })] =
        builtInWallPlaneAssemblyGroupForId(assemblyId);
      return acc;
    },
    {} as RWTable<Group["id"]>,
  );

  const defaultEntityGroups: Table<Group["id"]> = {
    ...defaultDatumsEntityGroups,
    ...defaultFramingComponentsEntityGroups,
    ...defaultMasonryComponentsEntityGroups,
    ...defaultSheathingComponentsEntityGroups,
    ...defaultCipComponentsEntityGroups,
    ...defaultHeaderAssembliesEntityGroups,
    ...defaultFoundationComponentsEntityGroups,
    ...defaultFoundationAssembliesEntityGroups,
    ...defaultFrameFloorAssembliesEntityGroups,
    ...defaultSlabFloorAssembliesEntityGroups,
    ...defaultSlabComponentsEntityGroups,
    ...defaultWallPlaneAssembliesEntityGroups,
  };

  let doc: Doc = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    name: name ?? DEFAULT_STUDIO_DOC_NAME,
    levels: {
      [levelUid]: {
        entityType: "LEVEL",
        id: levelUid,
        name: "Level 1",
        sortIndex: 0,
        topOfWall: levelInchesNew(108),
        enableAutoGeneratedFoundation: true,
        enableAutoGeneratedFloorSystem: true,
        placement: stackedLevelPlacementNew(),
      },
    },
    compositeLevels: emptyObject,
    rooms: emptyObject,
    roomLayouts: emptyObject,
    roomLayoutInstances: emptyObject,
    roofVolumes: emptyObject,
    entityRoomLayouts: emptyObject,
    modifiers: emptyObject,
    edgeModifiers: emptyObject,
    floors: emptyObject,
    floorFinishes: emptyObject,
    optionSets: Object.freeze({
      ...defaultOptionSets,
      [SystemOptionSet.ELEVATION_STYLE]: copyForElevationOptionSet(undefined, elevations),
    }),
    instanceOptionSets: emptyObject,
    optionRules: emptyObject,
    optionEffects: emptyObject,
    optionSetOverrides: emptyObject,
    optionLocks: emptyObject,
    docRefs: emptyObject,
    edgeBehaviors: emptyObject,
    entityRefs: emptyObject,
    annotation: emptyObject,
    view3Ds: emptyObject,
    planViews: emptyObject,
    dataTables: emptyObject,
    dataSequences: emptyObject,
    elevationViews: emptyObject,
    electricalDevices: emptyObject,
    electricalLayouts: emptyObject,
    ...defaultRoofSystemTables(blocks),
    foundationSystems: emptyObject,
    foundations: emptyObject,
    stairSystems: emptyObject,
    stairBlocks: emptyObject,
    stairCatalog: emptyObject,
    railings: emptyObject,
    railingPosts: emptyObject,
    blocks,
    openings: emptyObject,
    elevations,
    facades: emptyObject,
    facadeSpanStrata: emptyObject,
    facadeSweepStrata: emptyObject,
    locked: emptyEntitySet,
    hidden: emptyEntitySet,
    underlays: emptyObject,
    roofUnderlays: emptyObject,
    settingsSets: {
      [defaultSettingsSetId]: defaultSettingsSet,
    },
    entitySettings: emptyObject,
    optionTemplates: emptyObject,
    referenceGeom: emptyObject,
    palettes: emptyObject,
    settings: Object.freeze({
      [exteriorMaterialSettingsUid]: {
        id: exteriorMaterialSettingsUid,
        entityType: "SETTINGS",
        sortIndex: 0,
        name: snakeCaseToTitleCase(SettingsType.EXTERIOR_MATERIAL_SETTINGS),
        parentId: defaultSettingsSetId,
        type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
        palettes: [DEFAULT_EXTERIOR_PALETTE_ID],
        paletteSlotsUserDefinedNames: {
          [DEFAULT_EXTERIOR_PALETTE_ID]: {},
        },
      },
      [interiorMaterialSettingsUid]: {
        id: interiorMaterialSettingsUid,
        entityType: "SETTINGS",
        sortIndex: 1,
        name: snakeCaseToTitleCase(SettingsType.INTERIOR_MATERIAL_SETTINGS),
        parentId: defaultSettingsSetId,
        type: SettingsType.INTERIOR_MATERIAL_SETTINGS,
        palettes: [DEFAULT_INTERIOR_PALETTE_ID],
        paletteSlotsUserDefinedNames: {
          [DEFAULT_INTERIOR_PALETTE_ID]: {},
        },
      },
      [wainscotingSettingsUid]: {
        id: wainscotingSettingsUid,
        entityType: "SETTINGS",
        sortIndex: 2,
        name: snakeCaseToTitleCase(SettingsType.WAINSCOTING_SETTINGS),
        parentId: defaultSettingsSetId,
        type: SettingsType.WAINSCOTING_SETTINGS,
        wainscotingMode: StreetOrientationMode.NO_FACES,
      },
      [roofSettingsId]: {
        id: roofSettingsId,
        type: SettingsType.ROOF_SETTINGS,
        entityType: "SETTINGS",
        name: snakeCaseToTitleCase(SettingsType.ROOF_SETTINGS),
        sortIndex: 3,
        parentId: defaultSettingsSetId,
        useNewRoofTrimGeneration: true,
      },
    }) as Table<SettingsFragment>,
    sheets: emptyObject,
    drawingRefs: emptyObject,
    optionChoices: DEFAULT_OPTION_CHOICES,
    snapKinds: DEFAULT_SNAP_KINDS,
    snapEntities: DEFAULT_SNAP_ENTITIES,
    gridSnapDistance: 6,
    isParsed: true,
    presets: {
      [docDefaultPreset.id]: docDefaultPreset,
    },
    casework: emptyObject,
    ceilings: emptyObject,
    columns: emptyObject,
    wallPlaneAssemblies: builtInWallPlaneAssemblies,
    wallPlaneFilters: emptyObject,
    wallPlaneRules: emptyObject,
    wallPlaneTrims: emptyObject,
    freeformWalls: emptyObject,
    propInstances: emptyObject,
    roofOpenings: emptyObject,
    sites: emptyObject,
    siteShapes: emptyObject,
    sectionViews: emptyObject,
    dormers: emptyObject,
    skylights: emptyObject,
    groups: defaultGroups,
    entityGroups: defaultEntityGroups,
    attributes: defaultHomeAttributes,
    measurements: emptyObject,
    viewOverrides: emptyObject,
    selectors: emptyObject,
    viewSelectors: emptyObject,
    planSetTemplates: emptyObject,
    planSetTemplateConfigs: emptyObject,
    contentTemplates: emptyObject,
    contentReplicators: emptyObject,
    contentTemplateInstances: emptyObject,
    localParameters: emptyObject,
    globalParameters: emptyObject,
    viewports: emptyObject,
    viewTemplates: defaultViewTemplateTable(),
    viewTemplateOverrides: emptyObject,
    labels: defaultLabelTable(),
    reportableUnitDesignations: emptyObject,
    effects: emptyObject,
    datums: builtInDatums,
    ruleBasedSelectors: emptyObject,
    ruleBasedSelectorInstances: emptyObject,
    assets: emptyObject,
    propAttributes: emptyObject,
    profiles: { [ProfileExtrusionType.DEFAULT_PROFILE]: defaultProfileEntity },
    railingCatalog: emptyObject,
    facadeSpanStratumAssociations: emptyObject,
    facadeSweepStratumAssociations: emptyObject,
    componentMembers: emptyObject,
    componentAssociations: emptyObject,
    components: emptyObject,
    componentInstances: emptyObject,
    datumSpanAssociations: emptyObject,
    headerAssemblies: builtInHeaderAssemblies,
    framingComponents: builtInFramingComponents,
    masonryComponents: builtInMasonryComponents,
    cipComponents: builtInCipComponents,
    sheathingComponents: builtInSheathingComponents,
    foundationAssemblies: builtInFoundationAssemblies,
    foundationComponents: builtInFoundationComponents,
    frameFloorAssemblies: builtInFrameFloorAssemblies,
    slabFloorAssemblies: builtInSlabFloorAssemblies,
    slabComponents: builtInSlabFloorComponents,
    floorSystems: emptyObject,
  };

  doc = docInitDefaultViews(doc);

  return docSyncSettingsOptionSets(doc);
}

/**
 * Shared Doc used only to fill missing keys when parsing existing content.
 * Built once lazily so non-empty parses do not pay defaultDoc() allocation each load.
 *
 * Do not use this as a new empty document — call {@link defaultDoc} so each new doc
 * gets fresh entity UIDs. Consumers must only shallow-spread this template under
 * migrated content; never mutate it in place.
 */
let defaultDocFillTemplate: Doc | undefined;

/**
 * Seeded so the memoized template is identical in every process. A doc missing a table inherits the
 * template's copy, minted ids and all, and content hashes are compared across processes.
 */
const FILL_TEMPLATE_UID_SEED = 1;

export function getDefaultDocFillTemplate(): Doc {
  if (!defaultDocFillTemplate) {
    defaultDocFillTemplate = withSeededUids(FILL_TEMPLATE_UID_SEED, () => defaultDoc());
  }
  return defaultDocFillTemplate;
}

export function defaultStudioDoc(): ClientStudioDoc {
  return {
    id: "",
    lockedById: null,
    name: DEFAULT_STUDIO_DOC_NAME,
    publishedDoc: null,
    doc: defaultDoc(),
    publishedAt: null,
    updatedAt: new Date(),
    revision: 0,
    createdAt: new Date(),
    publishedVersionId: null,
    planImages: null,
    detached: false,
    detachedFromId: null,
    archivedAt: null,
    clientSessionId: null,
  };
}

function defaultBlockTable(defaultLevelId: string): Table<Block> {
  const id = uid();
  return { [id]: blockNew({ id, name: "Block", sortIndex: 0, levelId: defaultLevelId }) };
}

function defaultRoofSystemTables(blocks: Table<Block>): Pick<Doc, "roofs" | "roofSystems"> {
  const roofSystemId = uid();
  const roofId = uid();

  return {
    roofSystems: {
      [roofSystemId]: {
        entityType: "ROOF_SYSTEM",
        id: roofSystemId,
        name: "Roof System",
        sortIndex: 0,
      },
    },
    roofs: {
      [roofId]: roofNew(roofSystemId, roofId, CURRENT_GABLE_ROOF_VERSION, 0, "Roof", Object.keys(blocks), {}, []),
    },
  };
}

function defaultViewTemplateTable(): Table<ViewTemplate> {
  const planViewTemplate = viewTemplateNew({
    sortIndex: 0,
    viewType: "PLAN_VIEW",
    name: "Floor Plan",
    categories: FloorPlanTagOrder,
    referenceType: BuiltinPlanViewType.FLOOR,
    cutPlaneHeight: levelInchesNew(FLOOR_PLAN_CUT_PLANE),
    stairCutPlaneHeight: levelInchesNew(STAIR_CUT_HEIGHT),
  });
  const elevationViewTemplate = viewTemplateNew({
    sortIndex: 1,
    viewType: "ELEVATION_VIEW",
    name: "Elevation",
    categories: viewTemplateUICategoriesForViewType("ELEVATION_VIEW").filter(
      (category) => !sheetGeometryCategoriesExcludedFromDefault3DViewTemplates.includes(category),
    ),
  });
  const sectionViewTemplate = viewTemplateNew({
    sortIndex: 2,
    viewType: "SECTION_VIEW",
    name: "Section",
    categories: viewTemplateUICategoriesForViewType("SECTION_VIEW").filter(
      (category) => !sheetGeometryCategoriesExcludedFromDefault3DViewTemplates.includes(category),
    ),
  });
  const perspectiveViewTemplate = viewTemplateNew({
    sortIndex: 3,
    viewType: "VIEW_3D",
    name: "Perspective",
    categories: viewTemplateUICategoriesForViewType("VIEW_3D").filter(
      (category) => !sheetGeometryCategoriesExcludedFromDefault3DViewTemplates.includes(category),
    ),
  });
  const drawingViewTemplate = viewTemplateNew({
    sortIndex: 4,
    viewType: "DRAWING",
    name: "Drawing",
    categories: viewTemplateUICategoriesForViewType("DRAWING"),
  });

  return {
    [planViewTemplate.id]: planViewTemplate,
    [elevationViewTemplate.id]: elevationViewTemplate,
    [sectionViewTemplate.id]: sectionViewTemplate,
    [perspectiveViewTemplate.id]: perspectiveViewTemplate,
    [drawingViewTemplate.id]: drawingViewTemplate,
  };
}
